import React, { useState, useMemo, useCallback } from 'react';
import {
  User, Building, Shield, Bell, Palette, Globe, CreditCard, Users, Truck, Percent, GitGraph, Box,
  Handshake,
  PersonStanding,
  Code,
  PlusCircle,
  Cog,
  HeartHandshakeIcon,
  NotebookPenIcon,
  BanknoteArrowUp,
  Clipboard
} from 'lucide-react';
import useUserAccount from '../../../hooks/userAccountHook';
import ProfileDetails from './ProfileDetails';
import TeamMembers from '../../partners/TeamMembers';
import BusinessPreference from './BusinessPreference';
import Discounts from './discount';
import Vehicles from '../../admin/vehicles';
import Expenses from './expenses';
import CountriesInOperation from './countries';
import RolesAndPermissions from './RolesAndPermisions';
import Products from './products';
import Billing from './billing';
import Branch from './branch';
import Slack from './slack';
import useRoute from '../../../hooks/routesHook';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SecuritySettings from './Security';
import NotificationSettings from './NotificationSettings';
import AppearanceSettings from './AppearanceSettings';
import InsuranceCompanies from './Intermediaries';
import Button from '../../../components/form-control/Button';
import { IAction } from '../../../resources/interfaces';
import usePartner from '../../../hooks/partnerHook';
import Developers from './Developers';
import { COMPANY_TYPES, ACCESS_RIGHTS } from '../../../resources/enums';
import Page from '../../../components/PageWrapper';
import useBranch from '../../../hooks/branchHook';
import useProduct from '../../../hooks/productHook';
import useExpenses from '../../../hooks/expensesHook';
import useSlack from '../../../hooks/slackHook';
import useIntermediaries from '../../../hooks/intermediariesHook';
import useCountries from '../../../hooks/countryHook';
import useVehicle from '../../../hooks/vehicleHook';
import useDiscount from '../../../hooks/discountHook';
import InsuranceConfiguration from './insuranceConfiguration';
import useInsuranceConfiguration from '../../../hooks/insuranceConfigurationHook';
import { EllipsisVerticalIcon } from '@heroicons/react/24/solid';
import TermsAndConditions from './terms-and-conditions';
import useResizeObserver from '@react-hook/resize-observer';
import Tooltip from '../../../components/ToolTip';
import PopOnMe from '../../../components/elements/poponme';
import SettlementAccount from './SettlementAccount';
import Bills from '../../partners/shared/bills';
import AuditLogs from '../../admin/auditLogs';
import { TbTransfer } from 'react-icons/tb';
import useBilling from '../../../hooks/billingHook';
import ErrorBoundary from '../../../components/ErrorBoundary';
import { useDispatch } from 'react-redux';
import { SET_COMPANY_ONBOARDING_STATUS, SET_PENDING_ACTIONS } from '../../../states/slices/globalSlice';
import { useQuery } from '@tanstack/react-query';

interface SettingsSection {
  id: string;
  title: string;
  icon: React.ComponentType<any>;
  description: string;
  primaryAction?: IAction;
  companyType: COMPANY_TYPES[] | '*';
  access?: ACCESS_RIGHTS[];
}

export const SettingsPage: React.FC = () => {
  const { user } = useUserAccount();
  const { getDashboardBase } = useRoute();
  const navigate = useNavigate();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const { inviteMember, PartnerModal } = usePartner()
  const { createNewBranchHandler, BranchModal } = useBranch();
  const { addProduct, ProductModal } = useProduct();
  const { addExpense, ExpenseModal } = useExpenses();
  const { showAddWorkspaceModal, SlackModal } = useSlack();
  const { IntermediariesModal, handleCreateNewIntermediary } = useIntermediaries()
  const { addCountry, CountriesModal } = useCountries();
  const { addVehicle, VehicleModal } = useVehicle();
  const { AddNoClaimDiscount, AddCoverageType, InsuranceConfigurationModal } = useInsuranceConfiguration();
  const { showAddDiscount, DiscountModal } = useDiscount();
  const { BillingModal } = useBilling();
  const tab = queryParams.get('tab');
  const [insuranceConfigurationTab, setInsuranceConfigurationTab] = useState<string>('')
  const target = React.useRef(null);
  const [width, setWidth] = React.useState(0);
  const pageRef = React.useRef<HTMLDivElement>(null);

  useResizeObserver(target, (entry) => {
    setWidth(entry.contentRect.width);
  });

    const dispatch = useDispatch();
    const { fetchCompanyOnboardingStatus } = usePartner();
  
     const getCompanyOnboardingStatus = async () => {
    
            try {
                const data = await fetchCompanyOnboardingStatus();
                if (data) {
                    dispatch(SET_PENDING_ACTIONS(data?.pendingActions));
                    dispatch(SET_COMPANY_ONBOARDING_STATUS(data))
                }
            } catch (error) {
    
            }
        }
       const { } = useQuery({
          queryKey: ['getCompanyOnboardingStatus'],
          queryFn: getCompanyOnboardingStatus
        });
      
  const shouldCollapseTab = (item: string) => {

    switch (item) {
      case 'Terms & Conditions':
      case 'Developers':
      case 'Integrations':
      case 'Appearance':
      case 'Billing Plans':
      case 'Notifications':
      case 'Expenses':
      case 'Vehicles':
      case 'Slack Integration':
      case 'Roles & Permissions':
      case 'Countries':
      case 'Policy Configuration':
      case 'Discounts':
        return width < 990;
      case 'Products':
        return width < 920;
      case 'Security & Privacy':
        return width < 800;
      case 'Underwriters':
        return width < 610;
      case 'Branches':
        return width < 470;
    }
  }

  const getMoreTabsActions = () => {
    const actions: any[] = [];
    const userCompanyType = user?.company?.type;

    if (!userCompanyType) {
      return [];
    }

    const tabs = [
      { title: 'Terms & Conditions', tab: 'termsandconditions', companyType: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.BROKER], access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.MANAGER, ACCESS_RIGHTS.SUPERVISOR] },
      { title: 'Developers', tab: 'developers', companyType: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.INSURER], },
      { title: 'Integrations', tab: 'integrations', companyType: [COMPANY_TYPES.INTERNAL] },
      { title: 'Appearance', tab: 'appearance', companyType: [COMPANY_TYPES.INTERNAL] },
      { title: 'Billing Plans', tab: 'billing', companyType: [COMPANY_TYPES.INTERNAL] },
      { title: 'Notifications', tab: 'notifications', companyType: ['*'] },
      { title: 'Expenses', tab: 'expenses', companyType: [COMPANY_TYPES.INTERNAL] },
      { title: 'Vehicles', tab: 'vehicles', companyType: [COMPANY_TYPES.INTERNAL] },
      { title: 'Slack Integration', tab: 'slack', companyType: [COMPANY_TYPES.INTERNAL] },
      { title: 'Roles & Permissions', tab: 'roles', companyType: [COMPANY_TYPES.INTERNAL] },
      { title: 'Countries', tab: 'countries', companyType: [COMPANY_TYPES.INTERNAL] },
      { title: 'Policy Configuration', tab: 'configuration', companyType: [COMPANY_TYPES.INTERNAL] },
      { title: 'Discounts', tab: 'discounts', companyType: [COMPANY_TYPES.INTERNAL] },
      { title: 'Products', tab: 'products', companyType: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.INSURER] },
      { title: 'Security & Privacy', tab: 'security', companyType: ['*'] },
      { title: 'Underwriters', tab: 'insuranceCompanies', companyType: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.BROKER], access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.MANAGER, ACCESS_RIGHTS.SUPERVISOR] },
      { title: 'Brokers', tab: 'brokers', companyType: [COMPANY_TYPES.INSURER], access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.MANAGER, ACCESS_RIGHTS.SUPERVISOR] },
      { title: 'Branches', tab: 'branches', companyType: ['*'] },

    ]


    tabs.map((tab: { title: string, tab: string, companyType: string[], access?: ACCESS_RIGHTS[] }) => {
      // Respect access-rights restrictions (mirrors ALL_SECTIONS gating)
      if (tab.access && (!user?.accessRight || !tab.access.includes(user.accessRight))) {
        return;
      }
      if (tab.companyType.includes(userCompanyType) || tab.companyType.includes('*')) {
        if (shouldCollapseTab(tab.title)) {
          actions.push({
            label: tab.title,
            callback: () => navigate(`${getDashboardBase()}/settings?tab=${tab.tab}`)
          })
        }
      }

    })

    return actions.reverse();
  };

  const insuranceConfigTabs = (): { label: string; action: () => void } => {
    switch (insuranceConfigurationTab) {
      case "Insurance type":
        return { label: "Add Insurance Type", action: () => navigate(`${getDashboardBase()}/insurance-configuration/insurance-type?action=create`) };
      case "Body type":
        return { label: "Add Body type", action: () => { } };
      case "Risk type":
        return { label: "Add Risk type", action: () => navigate(`${getDashboardBase()}/insurance-configuration/insurance-risk-type?action=create`) };
      case "Coverage type":
        return { label: "Add Coverage type", action: () => AddCoverageType() };
      case "No claim discount":
        return { label: "Add Discount Rate", action: () => AddNoClaimDiscount() };

      default:
        return { label: "Add Risk type", action: () => navigate(`${getDashboardBase()}/insurance-configuration/insurance-risk-type?action=create`) };
    }
  }

  // Define all possible settings sections with their team types
  const ALL_SECTIONS: (SettingsSection & { companyType: COMPANY_TYPES[] | '*' })[] = [
    {
      id: 'profile',
      title: 'Profile',
      icon: User,
      description: 'Manage your personal information and preferences',
      companyType: '*',
    },
    {
      id: 'organization',
      title: 'Organization',
      icon: Building,
      description: 'Business information and configuration',
      companyType: '*',
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.MANAGER, ACCESS_RIGHTS.SUPERVISOR],
    },
    {
      id: 'settlementAccount',
      title: 'Settlement Account',
      icon: TbTransfer,
      description: 'Settlement account information and configuration',
      companyType: '*',
    },
    {
      id: 'users',
      title: 'Users',
      icon: Users,
      description: 'Manage system users and their accounts',
      companyType: '*',
      primaryAction: user?.isOwner() ? {
        label: 'Add User',
        callback: () => {
          inviteMember(user?.company, 'getAllCompanyStaff')
        },
        icon: <PersonStanding />,
      } : undefined
    },
    {
      id: 'branches',
      title: 'Branches',
      icon: GitGraph,
      description: 'Manage branches and their details',
      companyType: '*',
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.MANAGER, ACCESS_RIGHTS.SUPERVISOR],
      primaryAction: {
        label: 'New Branch',
        callback: () => createNewBranchHandler()
      }
    },
    {
      id: 'insuranceCompanies',
      title: 'Underwriters',
      icon: Handshake,
      description: 'Manage underwriters and their details',
      companyType: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.BROKER],
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.MANAGER, ACCESS_RIGHTS.SUPERVISOR],
      primaryAction: {
        label: 'Add Company',
        callback: () => handleCreateNewIntermediary(true)
      }
    },
    {
      id: 'brokers',
      title: 'Brokers',
      icon: Handshake,
      description: 'View your brokers and manage their policy issuance mode',
      companyType: [COMPANY_TYPES.INSURER],
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.MANAGER, ACCESS_RIGHTS.SUPERVISOR],
    },
    {
      id: 'security',
      title: 'Security & Privacy',
      icon: Shield,
      description: 'Password, two-factor authentication, and privacy settings',
      companyType: '*',
    },
    {
      id: 'products',
      title: 'Products',
      icon: Box,
      description: 'Manage insurance products and offerings',
      companyType: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.INSURER],
      primaryAction: {
        label: 'Add Product',
        callback: () => addProduct()
      }
    },
    {
      id: 'discounts',
      title: 'Discounts',
      icon: Percent,
      description: 'Manage discount codes and special offers',
      companyType: [COMPANY_TYPES.INTERNAL],
      primaryAction: {
        label: 'New Discount',
        callback: () => showAddDiscount(),
      }
    },
    {
      id: 'configuration',
      title: 'Policy Configuration',
      icon: Cog,
      description: 'Configure risk type, coverage and no claim discount',
      companyType: [COMPANY_TYPES.INTERNAL,],
    },
    {
      id: 'countries',
      title: 'Countries',
      icon: Globe,
      description: 'Manage supported countries and regions',
      companyType: [COMPANY_TYPES.INTERNAL],
      primaryAction: {
        label: 'Add Country',
        callback: () => addCountry(),
      }
    },
    {
      id: 'roles',
      title: 'Roles & Permissions',
      icon: Shield,
      description: 'Manage user roles and their permissions',
      companyType: [COMPANY_TYPES.INTERNAL],
      primaryAction: {
        label: 'Create custom role',
        callback: () => {
          navigate(`${getDashboardBase()}/settings/roles/create-role`)
        }
      }
    },
    {
      id: 'slack',
      title: 'Slack Integration',
      icon: Bell,
      description: 'Configure Slack notifications and integration',
      companyType: [COMPANY_TYPES.INTERNAL],
      primaryAction: {
        label: 'Add Workspace',
        callback: () => showAddWorkspaceModal()
      }
    },
    {
      id: 'vehicles',
      title: 'Vehicles',
      icon: Truck,
      description: 'Vehicle makes, models, and specifications',
      companyType: [COMPANY_TYPES.INTERNAL],
      primaryAction: {
        label: 'Add Vehicle',
        callback: () => addVehicle()
      }
    },
    {
      id: 'expenses',
      title: 'Expenses',
      icon: CreditCard,
      description: 'Track and manage business expenses',
      companyType: [COMPANY_TYPES.INTERNAL],
      primaryAction: {
        label: 'Add Expense',
        callback: () => addExpense()
      }
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: Bell,
      description: 'Email, SMS, and push notification preferences',
      companyType: '*'
    },
    {
      id: 'billing',
      title: 'Billing Plans',
      icon: CreditCard,
      description: 'Subscription, payment methods, and usage',
      companyType: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.BROKER],
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.MANAGER, ACCESS_RIGHTS.SUPERVISOR],
      primaryAction: user.isInternalAdmin() || user.isInternalOwner() ? {
        label: 'Add Subscription',
        icon: <NotebookPenIcon className='h-5 w-5 ' />,
        callback: () => navigate(`${getDashboardBase()}/settings/billing/subscription-plan`)
      } : undefined
    },
    {
      id: 'appearance',
      title: 'Appearance',
      icon: Palette,
      description: 'Theme, layout, and display preferences',
      companyType: '*',
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.MANAGER, ACCESS_RIGHTS.SUPERVISOR],
    },
    {
      id: 'integrations',
      title: 'Integrations',
      icon: Truck,
      description: 'Third-party services and API connections',
      companyType: [COMPANY_TYPES.INTERNAL],
    },
    {
      id: 'developers',
      title: 'Developers',
      icon: Code,
      description: 'Developers services',
      companyType: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.INSURER],
    },
    // Bills was previously a Settings tab on the insurer side after an earlier
    // sidebar→Settings migration; the migration is now reverted (Bills lives
    // in the sidebar for insurer again, see SIDE_BAR_NAVS.financials.bills),
    // so the Settings tab is removed to avoid duplication. The Bills route
    // and its render-case below continue to exist for direct deep-links.
    {
      id: 'audit',
      title: 'Audit Logs',
      icon: Clipboard,
      description: 'View a list of all user logs and associated details',
      companyType: [COMPANY_TYPES.INSURER],
      access: [ACCESS_RIGHTS.OWNER],
    },
    {
      id: 'termsandconditions',
      title: 'Terms & Conditions',
      icon: HeartHandshakeIcon,
      description: 'Terms and conditions',
      companyType: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.BROKER],
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.MANAGER, ACCESS_RIGHTS.SUPERVISOR],
    }
  ];

  // Get available sections based on company type and access rights
  const getAvailableSections = (): SettingsSection[] => {
    const userCompanyType = user?.company?.type;
    const userAccessRight = user?.accessRight;

    if (!userCompanyType) {
      return [];
    }

    return ALL_SECTIONS
      .filter(section => {
        // Include if section is for all company types
        if (section.companyType === '*') {
          // Check access rights if specified
          if (section.access && userAccessRight) {
            return section.access.includes(userAccessRight);
          }
          return !section.access; // Only show if no access restrictions
        }

        // Include if section includes user's company type
        if (Array.isArray(section.companyType)) {
          const companyTypeMatch = section.companyType.includes(userCompanyType);
          
          // Check access rights if specified
          if (section.access && userAccessRight) {
            return companyTypeMatch && section.access.includes(userAccessRight);
          }
          
          return companyTypeMatch && !section.access; // Only show if company matches and no access restrictions
        }

        return false;
      })
  };

  // Memoize available sections to prevent unnecessary recalculations
  const settingsSections = useMemo(() => getAvailableSections(), [user?.company?.type, user?.accessRight]);

  // Validate and set active tab with fallback to 'profile'
  const activeTab = useMemo(() => {
    const tabParam = queryParams.get('tab');
    return tabParam && settingsSections.some(s => s.id === tabParam) ? tabParam : 'profile';
  }, [queryParams, settingsSections]);

  // Memoize active section data to avoid repeated find() calls
  const activeSection = useMemo(() => 
    settingsSections.find(s => s.id === activeTab), 
    [settingsSections, activeTab]
  );

  // Memoize primary action to avoid repeated lookups
  const primaryAction = useMemo(() => {
    if (activeSection?.id === 'configuration') {
      const configAction = insuranceConfigTabs();
      return {
        label: configAction.label,
        callback: configAction.action,
        icon: <PlusCircle className='h-5 w-5 ' />
      };
    }
    return activeSection?.primaryAction;
  }, [activeSection, insuranceConfigurationTab]);

  // Memoize more tabs actions to prevent recalculations on resize
  const moreTabsActions = useMemo(() => getMoreTabsActions(), [user?.company?.type, width]);

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileDetails />;
      case 'security':
        return <SecuritySettings />;
      case 'notifications':
        return <NotificationSettings />;
      case 'appearance':
        return <AppearanceSettings />;
      case 'users':
        return <TeamMembers />;
      case 'branches':
        return <Branch isSettings={true} />;
      case 'insuranceCompanies':
        return <InsuranceCompanies isSettings={true} />;
      case 'brokers':
        return <InsuranceCompanies isSettings={true} />;
      case 'organization':
        return <BusinessPreference />;
      case 'settlementAccount':
        return <SettlementAccount />;
      case 'discounts':
        return <Discounts isSettings={true} />
      case 'configuration':
        return <InsuranceConfiguration handleActiveTab={(tab) => { setInsuranceConfigurationTab(tab) }} />
      case 'vehicles':
        return <Vehicles isSettings={true} />
      case 'expenses':
        return <Expenses isSettings={true} />
      case 'countries':
        return <CountriesInOperation isSettings={true} />
      case 'roles':
        return <RolesAndPermissions isSettings={true} />
      case 'products':
        return <Products isSettings={true} />
      case 'slack':
        return <Slack isSettings={true} />
      case 'billing':
        return <Billing />
      case 'integrations':
        return (
          <div className="text-center py-12">
            <Truck className="mx-auto h-12 w-12 text-gray-400 dark:text-darkBg-subText" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-darkBg-mainText">Integrations</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-darkBg-subText">Connect with third-party services and APIs.</p>
          </div>
        );
      case 'team':
        return (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400 dark:text-darkBg-subText" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-darkBg-mainText">Team Management</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-darkBg-subText">Manage user roles and permissions.</p>
          </div>
        );
      case 'developers':
        return <Developers />
      case 'bills':
        return <Bills isSettings={true} />
      case 'audit':
        return <AuditLogs isSettings={true} />
      case 'termsandconditions':
        return <TermsAndConditions />
      default:
        return <ProfileDetails />;
    }
  };

  React.useEffect(() => {
    if (pageRef.current) {
      pageRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [activeTab])

  return (
    <Page title="Settings" className="">
      <div className="" ref={pageRef}>
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-darkBg-mainText">Settings</h1>
          <p className="text-gray-600 dark:text-darkBg-subText">Manage your account preferences and system configuration</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">

          {/* Settings Navigation */}
          <div className="lg:w-1/4 hidden min-[1024px]:block ">
            <nav className="space-y-1">
              {getAvailableSections().map((section) => {
                const Icon = section.icon;
                return (
                  <Link key={section.id} to={`${getDashboardBase()}/settings?tab=${section.id}`}>
                    <div
                      key={section.id}
                      className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === section.id
                        ? 'bg-white dark:bg-darkBg text-gray-900 dark:text-darkBg-mainText'
                        : 'text-gray-600 dark:text-darkBg-subText hover:text-gray-900 dark:hover:text-darkBg-mainText hover:bg-white dark:hover:bg-darkBg-border'
                        }`}
                    >
                      <div className=''><Icon className="h-[20px] w-[20px] mr-3" /></div>
                      <div className="text-left">
                        <div>{section.title}</div>
                        <div className="text-xs text-gray-500 dark:text-darkBg-subText hidden lg:block">
                          {section.description}
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Settings Navigation mobile screen*/}

          <div ref={target} className={`lg:w-1/4  min-[1024px]:hidden `}>

            <nav className="space-y-1 flex items-center justify-between">
              <div className='flex'>
                {getMoreTabsActions().length > 0 && getAvailableSections().map((section, index) => {
                  const Icon = section.icon;
                  return (
                    <Link key={section.id} to={`${getDashboardBase()}/settings?tab=${section.id}`}>
                      <div
                        key={section.id}
                        className={`w-full flex items-center px-3 py-2 text-sm  ${shouldCollapseTab(section.title) && 'hidden'} font-medium rounded-md transition-colors ${activeTab === section.id
                          ? 'bg-white dark:bg-darkBg text-gray-900 dark:text-darkBg-mainText'
                          : 'text-gray-600 dark:text-darkBg-subText hover:text-gray-900 dark:hover:text-darkBg-mainText hover:bg-white dark:hover:bg-darkBg-border'
                          }`}
                      >
                        <Icon className="mr-3 h-5 w-5" />
                        <div className="text-left">
                          <div className='whitespace-nowrap'>{section.title}</div>
                          <div className="text-xs text-gray-500 dark:text-darkBg-subText hidden ">
                            {section.description}
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              <div className='min-[1024px]:hidden'>
                <Tooltip bgColor="bg-primary-surface" message={'Actions'}>
                  <PopOnMe actions={moreTabsActions}>
                    <div
                      className={`  rounded-md cursor-pointer px-[2px] py-[4px] flex items-center gap-1`}>

                      <EllipsisVerticalIcon className="h-5 w-5 text-gray-500 dark:text-darkBg-subText " />
                    </div>
                  </PopOnMe>
                </Tooltip>
              </div>

            </nav>



          </div>

          {/* Settings Content */}
          <div className="lg:w-3/4">
            <div className="bg-white dark:bg-darkBg shadow dark:shadow-darkBg-border rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-darkBg-border flex items-center justify-between bg-gray-50 dark:bg-darkBg-border rounded-lg">
                <div className="">
                  <div className="inline-flex items-center gap-2 mb-2">
                    <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
                    <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
                      {activeSection?.title}
                    </h2>
                  </div>


                  <p className="text-sm text-gray-500 dark:text-darkBg-subText mt-1">
                    {activeSection?.description}
                  </p>
                </div>

                {primaryAction && insuranceConfigurationTab !== 'Body type' && (
                  <Button
                    label={primaryAction.label}
                    onClick={primaryAction.callback}
                    icon={primaryAction.icon}
                    bgColor='primary'
                  />
                )}
              </div>

              {activeTab &&
                <div className="px-6 p-5">
                  <ErrorBoundary>
                    {renderContent()}
                  </ErrorBoundary>
                </div>}
              <PartnerModal />
              <BranchModal />
              <ProductModal />
              <ExpenseModal />
              <SlackModal />
              <IntermediariesModal />
              <CountriesModal />
              <VehicleModal />
              <DiscountModal />
              <InsuranceConfigurationModal />
              <BillingModal />
            </div>
          </div>
        </div>
      </div >
    </Page>
  );
};


export default SettingsPage;